import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, MessageCircle, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CommentThread } from '../notes/CommentThread';
import { notesApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { getActingOnBehalfOf } from '../../lib/sharedAccess';
import {
  FINDING_CATEGORIES, FINDING_STRENGTHS, categoryOf, categoryLabel, strengthOf,
  byImportance, byCertainty, earliestDate, yearSpan,
} from '../../lib/findings';

export const DISMISS_REASONS = [
  { value: 'not_right', label: "This isn't right" },
  { value: 'already_explained', label: 'I already know why' },
  { value: 'dont_want_to_see', label: "I don't want to see this" },
];
const reasonLabel = (r) => DISMISS_REASONS.find((d) => d.value === r)?.label;

const SORTS = {
  important: { label: 'Most pressing first', cmp: byImportance },
  certain: { label: 'Most certain first', cmp: byCertainty },
  date: { label: 'In date order', cmp: (a, b) => earliestDate(a).localeCompare(earliestDate(b)) || byImportance(a, b) },
};

// How many cards show before "Show more" — enough to get going, few enough
// that a long file doesn't land all at once.
const PAGE = 8;

const formatWhen = (d) => {
  if (!d) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return formatDate(d);
  if (/^\d{4}-\d{2}$/.test(d)) {
    const [y, m] = d.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  return d;
};

/** What the Overview card and this tab say when there's nothing to list. */
export const noticedEmptyMessage = (org, gapStatus, hasRecords) => (
  gapStatus === 'no_documents'
    ? `Once ${org.name} sends your records and they have been analysed, we compare them with what the rules say should be in your file.`
    : gapStatus === 'waiting'
      ? `We will check what might be missing once everything from ${org.name} has been analysed.`
      : hasRecords
        ? 'Nothing obvious is missing from what the rules require. As more is analysed, we will keep checking.'
        : 'Once records have been analysed, we compare them with what the rules say should be in your file.'
);

// What a finding rests on: the record's own words (never paraphrased),
// what the young person said that it answered, when, and a way into the
// record itself.
const Evidence = ({ finding, onOpenRecord }) => (
  <ul className="space-y-2 border-l-2 border-border pl-3" data-testid={`finding-evidence-${finding.id}`}>
    {finding.evidence.map((e, i) => (
      <li key={`${e.record_id}-${i}`} className="text-sm text-ink-70">
        {e.responds_to && <span className="block text-ink-55">You said: “{e.responds_to}”</span>}
        {e.quote && <span className="block text-ink">“{e.quote}”</span>}
        {e.note && <span className="block text-ink-55">{e.note}</span>}
        <span className="block text-ink-55">
          {[formatWhen(e.date), e.doc_type].filter(Boolean).join(' · ')}
          {e.record_id && onOpenRecord && (
            <>
              {' · '}
              <button type="button" className="underline underline-offset-2 hover:text-ink" onClick={() => onOpenRecord(e.record_id)}>
                Open the record
              </button>
            </>
          )}
        </span>
      </li>
    ))}
    {finding.evidence_count > finding.evidence.length && (
      <li className="text-sm text-ink-55">…and {finding.evidence_count - finding.evidence.length} more.</li>
    )}
  </ul>
);

const quiet = 'text-sm text-ink-55 underline underline-offset-2 hover:text-ink';

/**
 * One finding as a card, the way Collections shows a saved item: what it
 * is and when on top, the reading itself (folded to two lines until asked
 * for more, or behind a warning when it quotes something painful —
 * `sensitive`), then a bar with the comments on the left and Dismiss (or,
 * once dismissed, Put it back) on the right, and the conversation inside
 * the same box. Never the word "breach" or "failed" — see
 * docs/detection-design.md § 2.
 */
export const FindingCard = ({
  finding, orgId, comments = [], onCommentsChanged, onDismiss, onRestore, onOpenRecord,
  composing, onCompose, showCategory = true, isNew = false,
}) => {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(!finding.sensitive);
  const [picking, setPicking] = useState(false);
  const dismissed = !!onRestore;
  const acting = getActingOnBehalfOf();
  const readOnly = !!acting && acting.permission !== 'read_write';
  const count = comments.length;
  const hasMore = !!(finding.confidence || finding.source || finding.evidence?.length);
  const meta = [showCategory ? categoryLabel(categoryOf(finding)) : null, yearSpan(finding)].filter(Boolean).join(' · ');

  return (
    <li className={`rounded-lg p-5 sm:px-6 space-y-3 ${dismissed ? 'bg-inset/60' : 'bg-ground'}`} data-testid={`finding-${finding.id}`}>
      <div>
        {(meta || isNew) && (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {isNew && (
              <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-ink" title="New since you last looked" data-testid={`finding-new-${finding.id}`}>
                <span aria-hidden="true" className="w-[8px] h-[8px] rounded-full bg-brand" />
                New
              </span>
            )}
            {meta && <span className="pd-mono text-ink-55">{meta}</span>}
          </span>
        )}
        <h3 className="text-[17px] font-semibold text-ink mt-0.5">{finding.title}</h3>
      </div>

      {!revealed ? (
        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md bg-care-wash px-3 py-1.5 text-sm text-care-ink" data-testid={`finding-warning-${finding.id}`}>
          <span>This quotes your records about something that may be hard to read.</span>
          <button type="button" className="font-semibold underline underline-offset-2" onClick={() => { setRevealed(true); setOpen(true); }} data-testid={`finding-open-${finding.id}`}>
            Show
          </button>
        </p>
      ) : (
        <div className="space-y-2">
          <p className={`text-[15px] text-ink-70 ${open ? '' : 'line-clamp-2'}`}>{finding.detail}</p>
          {open && (
            <>
              {finding.confidence && <p className="text-sm text-ink-55">{finding.confidence}</p>}
              {finding.source && <p className="text-sm text-ink-55">Where this comes from: {finding.source}.</p>}
              {finding.evidence?.length > 0 && <Evidence finding={finding} onOpenRecord={onOpenRecord} />}
            </>
          )}
          <div className="flex flex-wrap gap-4">
            {(hasMore || (finding.detail || '').length > 140) && (
              <button type="button" className={quiet} onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`finding-more-${finding.id}`}>
                {open ? 'Show less' : 'Read more'}
              </button>
            )}
            {finding.sensitive && (
              <button type="button" className={quiet} onClick={() => { setRevealed(false); setOpen(false); }} data-testid={`finding-hide-${finding.id}`}>
                Hide
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {!orgId || (readOnly && !count) ? <span /> : (
          <button
            type="button"
            onClick={() => !readOnly && onCompose(!composing)}
            aria-label={count ? `${count} comment${count === 1 ? '' : 's'}${readOnly ? '' : ' — write another'}` : 'Comment'}
            aria-expanded={!!composing}
            className="-ml-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-ink-70 hover:text-ink hover:bg-inset transition-colors duration-state"
            data-testid={`finding-comment-${finding.id}`}
          >
            <MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.75} aria-hidden="true" />
            <span>{count || 'Comment'}</span>
          </button>
        )}
        {readOnly ? null : dismissed ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-55">
            {reasonLabel(finding.dismissed_reason) && <span>You said: {reasonLabel(finding.dismissed_reason)}</span>}
            <button type="button" className="font-semibold text-brand hover:text-brand-ink" onClick={() => onRestore(finding)} data-testid={`restore-${finding.id}`}>
              Put it back
            </button>
          </span>
        ) : picking ? (
          <span className="flex flex-wrap items-center gap-3" data-testid={`dismiss-reasons-${finding.id}`}>
            <span className="text-sm text-ink-55">Why?</span>
            {DISMISS_REASONS.map((r) => (
              <button key={r.value} type="button" className={quiet} onClick={() => { setPicking(false); onDismiss(finding, r.value); }} data-testid={`dismiss-reason-${finding.id}-${r.value}`}>
                {r.label}
              </button>
            ))}
            <button type="button" className="text-sm text-ink-55 hover:text-ink" onClick={() => setPicking(false)}>Cancel</button>
          </span>
        ) : (
          <button type="button" className="text-sm text-ink-55 hover:text-ink" onClick={() => setPicking(true)} data-testid={`dismiss-${finding.id}`}>
            Dismiss
          </button>
        )}
      </div>

      {orgId && (count > 0 || composing) && (
        <div className="ml-6 sm:ml-12 max-w-[68ch]">
          <CommentThread
            targetType="finding" targetId={finding.id} orgId={orgId} tone="surface" compact
            comments={comments} onChanged={onCommentsChanged}
            composerOpen={!!composing} onComposerChange={onCompose}
            prompt="Say what you know about this — what really happened, why it might be, or that it's wrong."
            testId={`finding-notes-${finding.id}`}
          />
        </div>
      )}
    </li>
  );
};

const NavItem = ({ id, label, count, fresh = 0, current, onChoose, testId }) => (
  <button type="button" onClick={() => onChoose(id)} aria-current={current === id ? 'page' : undefined}
    className={`flex justify-between items-center gap-3 px-3 py-2.5 rounded-md text-[15px] text-left ${current === id ? 'bg-inset text-ink font-semibold' : 'text-ink-70 hover:bg-inset/60'}`}
    data-testid={testId}>
    <span className="min-w-0 break-words">{label}</span>
    <span className="shrink-0 inline-flex items-center gap-2">
      {fresh > 0 && <span className="w-[8px] h-[8px] rounded-full bg-brand" role="img" aria-label={`${fresh} new`} data-testid={`${testId}-new`} />}
      <span className="pd-mono">{count}</span>
    </span>
  </button>
);

const Pill = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testId}
    className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors duration-state ${on ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-ground text-ink-70 border-rule hover:bg-inset'}`}>
    {children}
  </button>
);

/**
 * "What we noticed" as its own tab, laid out like Collections: the kinds
 * of thing noticed down the side as drawers ("Everything" first, "Dismissed"
 * last), the findings in the middle as cards, and a toolbar to search,
 * narrow by how sure the check is, and sort. Only a page of cards shows at
 * once. The drawer is in the URL (`?n=`) so the Overview can link straight
 * to one. Search runs here, over what's already on screen.
 */
export const OrgNoticed = ({
  org, findings, dismissed = [], gapStatus, hasRecords, review = { state: 'idle' }, showCloserRead, newIds,
  onDismiss, onRestore, onOpenRecord, onAskForGaps, nationSlot = null,
}) => {
  const [params, setParams] = useSearchParams();
  const drawer = params.get('n') || 'all';
  const setDrawer = (d) => setParams((p) => { const n = new URLSearchParams(p); if (d === 'all') n.delete('n'); else n.set('n', d); return n; }, { replace: true });
  const [search, setSearch] = useState('');
  const [strength, setStrength] = useState('all');
  const [onlyNew, setOnlyNew] = useState(false);
  const isNew = useCallback((f) => !!newIds?.has(f.id), [newIds]);
  const [sort, setSort] = useState('important');
  const [limit, setLimit] = useState(PAGE);
  const [comments, setComments] = useState({});
  const [composeFor, setComposeFor] = useState(null);

  const live = useMemo(() => findings || [], [findings]);
  const inDismissed = drawer === 'dismissed';
  const source = inDismissed ? dismissed : live;

  const idsKey = useMemo(() => [...live, ...dismissed].map((f) => f.id).sort().join('|'), [live, dismissed]);
  const loadComments = useCallback(async () => {
    const ids = idsKey ? idsKey.split('|') : [];
    if (!ids.length || !org?.id) { setComments({}); return; }
    try {
      const notes = (await notesApi.list({ kind: 'comment', target_type: 'finding', target_ids: ids, org_id: org.id })).data?.notes || [];
      const next = {};
      notes.filter((n) => n.kind === 'comment' && n.target_type === 'finding').forEach((n) => { (next[n.target_id] = next[n.target_id] || []).push(n); });
      setComments(next);
    } catch { /* comments are extra; the findings still show */ }
  }, [idsKey, org?.id]);
  useEffect(() => { loadComments(); }, [loadComments]);

  const counts = useMemo(() => {
    const c = {}; const fresh = {};
    live.forEach((f) => {
      c[categoryOf(f)] = (c[categoryOf(f)] || 0) + 1;
      if (isNew(f)) fresh[categoryOf(f)] = (fresh[categoryOf(f)] || 0) + 1;
    });
    return { c, fresh, freshTotal: Object.values(fresh).reduce((a, b) => a + b, 0) };
  }, [live, isNew]);

  const inDrawer = useMemo(
    () => (drawer === 'all' || inDismissed ? source : source.filter((f) => categoryOf(f) === drawer)),
    [source, drawer, inDismissed],
  );
  const strengthsHere = useMemo(() => FINDING_STRENGTHS.filter((s) => inDrawer.some((f) => strengthOf(f) === s.id)), [inDrawer]);
  const shown = useMemo(() => {
    let list = strength === 'all' ? inDrawer : inDrawer.filter((f) => strengthOf(f) === strength);
    if (onlyNew) list = list.filter(isNew);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((f) => [
        f.title, f.detail, f.source, ...(f.evidence || []).flatMap((e) => [e.quote, e.responds_to, e.note, e.doc_type]),
        ...(comments[f.id] || []).map((n) => n.text),
      ].some((t) => t && String(t).toLowerCase().includes(q)));
    }
    // What's new since last time comes first, whichever order is chosen —
    // it's what the dot that brought them here was about.
    return [...list].sort((a, b) => (isNew(b) - isNew(a)) || SORTS[sort].cmp(a, b));
  }, [inDrawer, strength, onlyNew, isNew, search, sort, comments]);
  const freshHere = useMemo(() => (inDismissed ? 0 : inDrawer.filter(isNew).length), [inDrawer, inDismissed, isNew]);

  // A different drawer, filter or search starts from the top again.
  useEffect(() => { setLimit(PAGE); }, [drawer, strength, onlyNew, search, sort]);
  useEffect(() => { if (onlyNew && !freshHere) setOnlyNew(false); }, [onlyNew, freshHere]);
  useEffect(() => { if (strength !== 'all' && !strengthsHere.some((s) => s.id === strength)) setStrength('all'); }, [strengthsHere, strength]);

  if (findings === null) {
    return <p className="text-sm text-ink-55 flex items-center gap-2" data-testid="org-noticed-loading"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Checking what should be here…</p>;
  }

  const drawerLabel = inDismissed ? 'Dismissed' : drawer === 'all' ? 'Everything' : categoryLabel(drawer);
  const drawerHint = FINDING_CATEGORIES.find((c) => c.id === drawer)?.hint;
  const current = search.trim()
    ? `${shown.length} of ${inDrawer.length} match “${search.trim()}”`
    : `${drawerLabel} · ${shown.length}`;

  return (
    <div className="space-y-6" data-testid="org-noticed">
      <div className="max-w-[72ch] space-y-3">
        <p className="text-[15px] text-ink-70" data-testid="org-findings-note">
          These are things an automated check noticed in the records you hold. It only knows what is in
          those records — it can&apos;t see the bigger picture, it can&apos;t tell whether something was
          left out of what {org.name} sent, and it can be wrong about any one of them. Use them as
          pointers to look into, not as answers.
        </p>
        {nationSlot}
        {showCloserRead && <CloserReadStatus review={review} />}
      </div>

      {live.length === 0 && dismissed.length === 0 ? (
        <div className="rounded-lg bg-ground py-10 px-6 text-center">
          <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">Nothing to show yet</p>
          <p className="text-base text-ink-55 max-w-md mx-auto" data-testid="org-noticed-empty">{noticedEmptyMessage(org, gapStatus, hasRecords)}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
          <nav className="rounded-lg bg-ground p-2 flex flex-col gap-0.5 lg:sticky lg:top-4" aria-label="Kinds of thing we noticed">
            <NavItem current={drawer} onChoose={setDrawer} id="all" label="Everything" count={live.length} fresh={counts.freshTotal} testId="noticed-drawer-all" />
            {FINDING_CATEGORIES.filter((c) => counts.c[c.id] || drawer === c.id).map((c) => (
              <NavItem current={drawer} onChoose={setDrawer} key={c.id} id={c.id} label={c.label} count={counts.c[c.id] || 0} fresh={counts.fresh[c.id]} testId={`noticed-drawer-${c.id}`} />
            ))}
            {(dismissed.length > 0 || inDismissed) && (
              <div className="mt-1 pt-1 border-t border-rule">
                <NavItem current={drawer} onChoose={setDrawer} id="dismissed" label="Dismissed" count={dismissed.length} testId="noticed-drawer-dismissed" />
              </div>
            )}
          </nav>

          <section className="space-y-4 min-w-0" aria-live="polite">
            {(drawerHint || inDismissed) && (
              <p className="text-[15px] text-ink-55">
                {inDismissed ? 'Things you put aside. They stay here, with why, in case you want one back.' : drawerHint}
              </p>
            )}
            {inDrawer.length > 0 && (
              <>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search what we noticed" aria-label="Search what we noticed" className="pr-10" data-testid="noticed-search" />
                    <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-ink-55" aria-hidden="true" />
                  </div>
                  <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger className="sm:w-[190px]" aria-label="Sort"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SORTS).map(([id, s]) => <SelectItem key={id} value={id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="pd-eyebrow mr-1" data-testid="noticed-current">{current}</span>
                  {freshHere > 0 && (
                    <Pill on={onlyNew} onClick={() => setOnlyNew((v) => !v)} testId="noticed-only-new">New · {freshHere}</Pill>
                  )}
                  {strengthsHere.length > 1 && (
                    <>
                      <Pill on={strength === 'all'} onClick={() => setStrength('all')} testId="noticed-strength-all">All</Pill>
                      {strengthsHere.map((s) => (
                        <Pill key={s.id} on={strength === s.id} onClick={() => setStrength(s.id)} testId={`noticed-strength-${s.id}`}>{s.label}</Pill>
                      ))}
                    </>
                  )}
                  {!inDismissed && onAskForGaps && live.some((f) => !!f.request_text) && (
                    <Button size="sm" variant="secondary" className="ml-auto" onClick={onAskForGaps} data-testid="org-ask-for-gaps">
                      Ask them for what is missing
                    </Button>
                  )}
                </div>
              </>
            )}

            {inDrawer.length === 0 ? (
              <div className="rounded-lg bg-ground py-10 px-6 text-center">
                <p className="text-base text-ink-55">{inDismissed ? 'Nothing dismissed.' : 'Nothing here now — you’ve dismissed everything in this drawer.'}</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="rounded-lg bg-ground py-10 px-6 text-center">
                <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">Nothing matches that</p>
                <p className="text-base text-ink-55">Try another word, or show everything.</p>
              </div>
            ) : (
              <>
                <ul className="flex flex-col gap-4">
                  {shown.slice(0, limit).map((f) => (
                    <FindingCard
                      key={f.id} finding={f} orgId={org.id} showCategory={drawer === 'all' || inDismissed} isNew={!inDismissed && isNew(f)}
                      comments={comments[f.id] || []} onCommentsChanged={loadComments}
                      composing={composeFor === f.id} onCompose={(o) => setComposeFor(o ? f.id : null)}
                      onDismiss={inDismissed ? undefined : onDismiss} onRestore={inDismissed ? onRestore : undefined}
                      onOpenRecord={onOpenRecord}
                    />
                  ))}
                </ul>
                {shown.length > limit && (
                  <div className="flex justify-center">
                    <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE)} data-testid="noticed-more">
                      Show {Math.min(PAGE, shown.length - limit)} more of {shown.length - limit}
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

/** Where the AI's closer read of the whole file stands, in one line. */
export const CloserReadStatus = ({ review }) => (
  <div data-testid="org-closer-read">
    {review.state === 'auto' ? (
      <p className="text-sm text-ink-55 flex items-center gap-2" data-testid="org-closer-read-auto">
        <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" />
        {review.writtenAt
          ? 'New documents arrived, so the whole file is being read again. When it’s done, this list will be brought up to date.'
          : 'Now everything from them has been analysed, an AI is reading the whole file and will bring what it finds together with the checks into one list — you can leave this page.'}
      </p>
    ) : review.state === 'running' ? (
      <p className="text-sm text-ink-55 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" />
        {review.steps > 2
          ? `Reading the whole file and bringing it together with the checks — ${review.done} of ${review.steps} steps done. This can take a few minutes; you can keep using the page.`
          : 'Reading the whole file and bringing it together with the checks — this can take a minute…'}
      </p>
    ) : (
      <p className="text-sm text-ink-55" data-testid="org-closer-read-status">
        {review.state === 'done'
          ? (review.count
            ? `An AI read ${review.read} of ${review.total} records and brought what it found together with the checks, so each thing is listed once. Every quote was checked against your records.`
            : `An AI read ${review.read} of ${review.total} records and didn't find anything more to raise.`)
            + (review.partsFailed ? ' Part of the file couldn’t be read this time; it will be read again when new documents arrive.' : '')
            + (review.writtenAt ? ` Read ${formatDate(review.writtenAt)}.` : '')
          : review.state === 'locked'
            ? 'Unlock your account first — a closer read needs your key to read the records.'
            : review.state === 'unavailable'
              ? 'A closer read isn’t available right now.'
              : review.state === 'error'
                ? 'The closer read didn’t work this time. We’ll try again next time you open this page, or when new documents arrive.'
                : 'Once everything from them has been analysed, an AI reads the whole file for things the checks can’t see, like a worry that was never followed up, and brings it together with the checks so each thing is listed once. It can be wrong; every quote it gives is checked against your records first.'}
      </p>
    )}
  </div>
);

export default OrgNoticed;
